import { axiosInstance } from "api/axios-config/axios-config";
import {
  AddUpdateWorkOrderPoRequest,
  UpdateWorkOrderPoRequest,
} from "api/models/request/work-order-po/add-update-wo-po";
import {
  IUpdateWorkOrderPOPackageProgressDto,
  IUpdateWorkOrderPOProgressDto,
} from "api/models/request/work-order-po/work-order-po-process-update";
import { Pagination } from "api/models/response/pagination";
import { DEFAULT_VOUCHER_SLICE_SIZE } from "commons/constants";
import { mapToDistinctOptionItems } from "commons/ultilities/option-item/option-item-ultil";
import { ICatalogue } from "mobx-stores/_models/base";
import {
  IUpdateWorkOrderPoStatus,
  IWorkOrderPoProperties,
} from "mobx-stores/_models/work-order-po/work-order-po";
import { BaseBussiness } from "../base";

class WorkOrderPoBusiness extends BaseBussiness<
  IWorkOrderPoProperties,
  AddUpdateWorkOrderPoRequest,
  UpdateWorkOrderPoRequest,
  IWorkOrderPoProperties
> {
  url = "";
  constructor(url: string) {
    super(url);
    this.url = url;
  }

  public searchWoPoAsOptionItems = async (
    keyword: string,
    customerId?: string,
  ) => {
    const wos = await this.getLists({
      filter: keyword,
      maxResultCount: DEFAULT_VOUCHER_SLICE_SIZE,
      skipCount: 0,
      sorting: "",
      customerId: customerId,
    });
    return mapToDistinctOptionItems(
      wos.items,
      (x) => x.id,
      (x) => x.woNumber,
    );
  };

  public getWorkOrderPoStatuses = async () => {
    try {
      const res = await axiosInstance.get<Pagination<ICatalogue>>(
        this.url + "/work-order-po-status",
      );
      return res.data;
    } catch (error) {
      console.error("Fail getWorkOrderPoStatuses", error);
      throw error;
    }
  };

  public updateStatus = async (
    workOrderPoId: string,
    data: IUpdateWorkOrderPoStatus,
  ) => {
    try {
      const res = await axiosInstance.put(
        `${this.url}/${workOrderPoId}/status`,
        data,
      );
      return res.data;
    } catch (error) {
      console.error("Fail updateStatus", error);
      return Promise.reject(error);
    }
  };

  private updateProgress = async <T>(
    workOrderPoId: string,
    processPath: string,
    data: T,
  ) => {
    try {
      const res = await axiosInstance.put(
        `${this.url}/${workOrderPoId}/${processPath}`,
        data,
      );
      return res.data;
    } catch (error) {
      console.error(`Fail update ${processPath}`, error);
      return Promise.reject(error);
    }
  };

  public updatePaperOutputProgress = async (
    workOrderPoId: string,
    data: IUpdateWorkOrderPOProgressDto,
  ) => {
    return this.updateProgress(workOrderPoId, "paper-output-progress", data);
  };

  public updatePrintProgress = async (
    workOrderPoId: string,
    data: IUpdateWorkOrderPOProgressDto,
  ) => {
    return this.updateProgress(workOrderPoId, "print-progress", data);
  };

  public updateMatteProgress = async (
    workOrderPoId: string,
    data: IUpdateWorkOrderPOProgressDto,
  ) => {
    return this.updateProgress(workOrderPoId, "matte-progress", data);
  };

  public updateAcceptanceProgress = async (
    workOrderPoId: string,
    data: IUpdateWorkOrderPOProgressDto,
  ) => {
    return this.updateProgress(workOrderPoId, "acceptance-progress", data);
  };

  public updateFoilProgress = async (
    workOrderPoId: string,
    data: IUpdateWorkOrderPOProgressDto,
  ) => {
    return this.updateProgress(workOrderPoId, "foil-progress", data);
  };

  public updateShapeProgress = async (
    workOrderPoId: string,
    data: IUpdateWorkOrderPOProgressDto,
  ) => {
    return this.updateProgress(workOrderPoId, "shape-progress", data);
  };

  public updatePasteProgress = async (
    workOrderPoId: string,
    data: IUpdateWorkOrderPOProgressDto,
  ) => {
    return this.updateProgress(workOrderPoId, "paste-progress", data);
  };

  public updateCutProgress = async (
    workOrderPoId: string,
    data: IUpdateWorkOrderPOProgressDto,
  ) => {
    return this.updateProgress(workOrderPoId, "cut-progress", data);
  };

  public updatePackageProgress = async (
    workOrderPoId: string,
    data: IUpdateWorkOrderPOPackageProgressDto,
  ) => {
    return this.updateProgress(workOrderPoId, "package-progress", data);
  };
}

export const workOrderPoBusiness = new WorkOrderPoBusiness(
  "/api/app/work-order-po",
);
